/**
 * UsersView (A7) -- who holds what, in the product.
 *
 * Users could not be seen or changed anywhere in MARSAD, so an
 * administrator's second job was a typed Keycloak URL under different
 * credentials. After the CX authority work there are four distinct
 * things a person can hold -- admin, a position, a job role, a module
 * licence -- and no surface said who held which, which is how the
 * authority defects survived in the first place.
 *
 * Read-only by design; creating and changing users stays in Keycloak,
 * one click away.
 */
import { useTranslation } from "react-i18next";

import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useUsers, type UserRow } from "shared/admin/useUsers";
import {
  accountConsoleUrl,
  adminConsoleWithheld,
  adminUsersUrl,
} from "shared/auth/identityLinks";

/**
 * D1 (round 8) -- the row that takes you to THAT person.
 *
 * Second round of "zero buttons, and the Keycloak console root in a
 * second tab as the only management route". Managing users stays in
 * Keycloak by design, and this does not change that: it is a better
 * door, not a new one. The administrator who wants to give somebody the
 * CRO role no longer has to find the realm, find users and search for a
 * name they are already looking at.
 *
 * Null without the subject -- a link built on a guessed id would open
 * the console on the wrong person, which is worse than no link.
 */
function keycloakUserUrl(subject: string | null | undefined): string | null {
  const base = adminUsersUrl();
  if (!base || !subject) return null;
  return `${base}/${encodeURIComponent(subject)}/settings`;
}

function Chips({
  values,
  tone,
  emptyLabel,
}: {
  values: string[];
  tone: string;
  // D12 -- a bare "--" read as "we do not know", and for somebody who
  // has signed in we do: they hold none. Not knowing and holding none
  // are different answers, and the column was giving the same mark for
  // both.
  emptyLabel?: string;
}) {
  if (values.length === 0) {
    return <span className="text-xs italic text-slate-400">{emptyLabel ?? "--"}</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {values.map((v) => (
        <span
          key={v}
          className={`rounded px-1.5 py-0.5 text-[10.5px] font-medium ${tone}`}
        >
          {v.replace(/_/g, " ")}
        </span>
      ))}
    </span>
  );
}

function Row({ user, isMe }: { user: UserRow; isMe: boolean }) {
  const { t } = useTranslation();
  const manageUrl = keycloakUserUrl(user.keycloak_subject);
  const accountUrl = accountConsoleUrl();
  return (
    <tr
      data-testid={`users-row-${user.username}`}
      className={user.disabled ? "opacity-50" : undefined}
    >
      <td className="px-3 py-2 align-top">
        <span className="block text-sm font-medium text-slate-900">
          {user.display_name || user.username}
        </span>
        <span className="block font-mono text-[11px] text-slate-500">
          {user.username}
          {user.email ? ` · ${user.email}` : ""}
        </span>
      </td>
      <td className="px-3 py-2 align-top">
        {user.is_admin ? (
          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10.5px] font-medium text-white">
            {t("screens.usersView.admin")}
          </span>
        ) : (
          <span className="text-xs text-slate-400">--</span>
        )}
      </td>
      {/* E5 -- these are REFLECTED from the token at sign-in, so a
          person who has never signed in shows an empty cell. A dash
          there reads as "holds no position", which is a claim; the
          truth is that we have not seen them yet. */}
      <td className="px-3 py-2 align-top">
        {user.last_login_at === null ? (
          <span
            data-testid="users-never-signed-in"
            className="text-xs italic text-slate-400"
          >
            {t("screens.usersView.notSeenYet")}
          </span>
        ) : (
          <Chips
            values={user.positions}
            tone="bg-sky-100 text-sky-800"
            emptyLabel={t("screens.usersView.holdsNone")}
          />
        )}
      </td>
      <td className="px-3 py-2 align-top">
        {user.last_login_at === null ? (
          <span
            data-testid="users-never-signed-in"
            className="text-xs italic text-slate-400"
          >
            {t("screens.usersView.notSeenYet")}
          </span>
        ) : (
          <Chips
            values={user.job_roles}
            tone="bg-amber-100 text-amber-900"
            emptyLabel={t("screens.usersView.holdsNone")}
          />
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <Chips values={user.modules} tone="bg-slate-100 text-slate-700" />
      </td>
      <td className="px-3 py-2 align-top text-xs text-slate-500">
        {user.last_login_at
          ? new Date(user.last_login_at).toISOString().slice(0, 16).replace("T", " ")
          : t("adminUsers.never")}
      </td>
      {/* D1 -- the row's own management route. */}
      <td className="px-3 py-2 align-top text-xs">
        {/* Your own row: your password is changed on your own account
            page, which every deployment serves -- not in the
            administrator's console, which the hosted edition does not. */}
        {isMe && accountUrl ? (
          <a
            href={accountUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`users-account-${user.username}`}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 font-medium text-brand-brassDark hover:border-brand-brass"
          >
            {t("adminUsers.yourAccount")}
            {/* Round 17 K20 -- say that it opens a new tab. */}
            <span aria-hidden="true"> ↗</span>
            <span className="sr-only"> {t("auth.opensNewTab")}</span>
          </a>
        ) : adminConsoleWithheld() ? (
          <span
            data-testid={`users-manage-withheld-${user.username}`}
            className="text-slate-500"
          >
            {t("adminUsers.manageWithheld")}
          </span>
        ) : manageUrl ? (
          <a
            href={manageUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`users-manage-${user.username}`}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-0.5 font-medium text-brand-brassDark hover:border-brand-brass"
          >
            {t("adminUsers.manageThisUser")}
          </a>
        ) : (
          <span className="italic text-slate-400">
            {/* Without the subject the console link would open on the
                wrong person, which is worse than none. */}
            {t("adminUsers.manageUnavailable")}
          </span>
        )}
      </td>
    </tr>
  );
}

export function UsersView() {
  const { t } = useTranslation();
  const me = useMe();
  const users = useUsers();
  const kcUrl = adminUsersUrl();
  const withheld = adminConsoleWithheld();
  const accountUrl = accountConsoleUrl();

  if (me.data && !me.data.is_admin) {
    return (
      <article className="max-w-3xl">
        <h1 className="font-display text-2xl text-slate-900">
          {t("adminUsers.heading")}
        </h1>
        <p
          data-testid="users-forbidden"
          className="mt-3 rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          {t("adminUsers.forbidden")}
        </p>
      </article>
    );
  }

  return (
    <article data-testid="users-view" className="max-w-5xl space-y-5">
      <header>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="dual-signoff"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("adminUsers.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-700">
          {t("adminUsers.explainer")}
        </p>
        <p
          data-testid="admin-users-executive-note"
          className="mt-2 max-w-3xl text-sm text-slate-600"
        >
          {t("adminUsers.explainerExecutive")}
        </p>
      </header>

      {/* The limitation, said out loud. A list that silently omits
          people is worse than no list. */}
      <p
        data-testid="users-reflected-note"
        className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
      >
        {t("adminUsers.reflectedNote")}
        {withheld ? (
          // ROUND 15 K3 kept the console off the public address and this
          // page went on linking to it. Say where it is instead.
          <>
            {" "}
            <span data-testid="users-keycloak-where">
              {t("adminUsers.manageWhere")}
            </span>
          </>
        ) : kcUrl ? (
          <>
            {" "}
            <a
              href={kcUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="users-keycloak-link"
              className="text-brand-brassDark hover:underline"
            >
              {t("adminUsers.manageInKeycloak")}
            </a>
          </>
        ) : (
          // Without the identity-provider URL we cannot build the link.
          // Saying so beats going quiet: otherwise the panel states that
          // users arrive at login and names no way to change one.
          <>
            {" "}
            <span data-testid="users-keycloak-unknown">
              {t("adminUsers.manageUnknown")}
            </span>
          </>
        )}
        {/* D10 -- what happens after you invite one.
            The page said users arrive at login and pointed at Keycloak,
            and stopped. The administrator's real question is whether the
            person will get what is waiting for them, and until this
            round the answer was no: recipients resolved from the
            reflected row, written at first login, so a ceremony raised
            for somebody who had not been here yet reached nobody. It is
            held for them now, so the page can say so. */}
        <span className="block pt-1 text-xs text-slate-500">
          {t("adminUsers.invitedArrivesWithWork")}
        </span>
        {accountUrl && (
          <span className="block pt-1 text-xs text-slate-500">
            {t("adminUsers.ownPasswordLead")}{" "}
            <a
              href={accountUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="users-own-account-link"
              className="text-brand-brassDark hover:underline"
            >
              {t("adminUsers.yourAccount")}
              <span aria-hidden="true"> ↗</span>
              <span className="sr-only"> {t("auth.opensNewTab")}</span>
            </a>
          </span>
        )}
      </p>
      <p data-testid="users-why-not-here" className="text-xs text-ink-2">
        {t("adminUsers.whyNotHere")}
      </p>

      {users.isLoading && (
        <p className="text-sm text-slate-400">{t("adminUsers.loading")}</p>
      )}
      {users.isError && (
        <p data-testid="users-error" className="text-sm text-rose-700">
          {users.error.detail}
        </p>
      )}

      {users.data && (
        <table className="w-full border-collapse text-start">
          <thead>
            <tr className="border-b border-slate-200 text-start">
              {[
                "person",
                "admin",
                "position",
                "job",
                "modules",
                "lastLogin",
                "manage",
              ].map((k) => (
                <th
                  key={k}
                  className="px-3 py-2 text-start font-mono text-[10.5px] uppercase tracking-[0.16em] text-slate-500"
                >
                  {t(`adminUsers.col.${k}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.data.users.map((u) => (
              <Row key={u.id} user={u} isMe={u.username === me.data?.username} />
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
}
